import {
	ChevronDown,
	ChevronRight,
	File,
	FileCode,
	FileJson,
	FileText,
	Folder,
	FolderOpen,
	Image,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import type { WorkspaceEntry } from "../lib/agent-api";
import { cn } from "../lib/utils";

interface FileTreeProps {
	entries: WorkspaceEntry[];
	selectedPath?: string;
	onSelect: (path: string, entry: WorkspaceEntry) => void;
	onDelete?: (path: string) => void;
	basePath?: string;
}

function getFileIcon(name: string) {
	const ext = name.split(".").pop()?.toLowerCase();
	if (["ts", "tsx", "js", "jsx"].includes(ext || ""))
		return <FileCode className="w-3.5 h-3.5 text-yellow-400" />;
	if (["json"].includes(ext || "")) return <FileJson className="w-3.5 h-3.5 text-green-400" />;
	if (["md", "txt"].includes(ext || "")) return <FileText className="w-3.5 h-3.5 text-blue-400" />;
	if (["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(ext || ""))
		return <Image className="w-3.5 h-3.5 text-purple-400" />;
	return <File className="w-3.5 h-3.5 text-muted-foreground" />;
}

function TreeNode({
	entry,
	path,
	selectedPath,
	onSelect,
	onDelete,
}: {
	entry: WorkspaceEntry;
	path: string;
	selectedPath?: string;
	onSelect: (path: string, entry: WorkspaceEntry) => void;
	onDelete?: (path: string) => void;
}) {
	const [expanded, setExpanded] = useState(false);
	const isDir = entry.type === "directory";
	const isSelected = selectedPath === path;
	const hasChildren = isDir && entry.children && entry.children.length > 0;

	return (
		<div>
			<button
				type="button"
				onClick={() => {
					if (isDir) {
						setExpanded(!expanded);
					}
					onSelect(path, entry);
				}}
				className={cn(
					"w-full flex items-center gap-1.5 px-2 py-1 text-xs rounded-md transition-colors group",
					isSelected ? "bg-primary/10 text-primary" : "hover:bg-secondary/50 text-foreground"
				)}
			>
				{isDir ? (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							setExpanded(!expanded);
						}}
						className="p-0.5 rounded hover:bg-secondary shrink-0"
					>
						{expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
					</button>
				) : (
					<span className="w-4 shrink-0" />
				)}
				{isDir ? (
					expanded ? (
						<FolderOpen className="w-3.5 h-3.5 text-primary shrink-0" />
					) : (
						<Folder className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
					)
				) : (
					<span className="shrink-0">{getFileIcon(entry.name)}</span>
				)}
				<span className="truncate flex-1 text-left">{entry.name}</span>
				{!isDir && onDelete && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onDelete(path);
						}}
						className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-destructive/10 hover:text-destructive transition-opacity shrink-0"
						title="Delete"
					>
						<Trash2 className="w-3 h-3" />
					</button>
				)}
			</button>
			{isDir && expanded && hasChildren && (
				<div className="ml-3 border-l border-border pl-1">
					{entry.children?.map((child) => {
						const childPath = path === "/" ? `/${child.name}` : `${path}/${child.name}`;
						return (
							<TreeNode
								key={childPath}
								entry={child}
								path={childPath}
								selectedPath={selectedPath}
								onSelect={onSelect}
								onDelete={onDelete}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

export function FileTree({ entries, selectedPath, onSelect, onDelete }: FileTreeProps) {
	if (entries.length === 0) {
		return (
			<div className="px-3 py-4 text-xs text-muted-foreground text-center">
				No files yet.
				<br />
				Ask the agent to create something.
			</div>
		);
	}

	return (
		<div className="space-y-0.5">
			{entries.map((entry) => (
				<TreeNode
					key={entry.name}
					entry={entry}
					path={entry.name}
					selectedPath={selectedPath}
					onSelect={onSelect}
					onDelete={onDelete}
				/>
			))}
		</div>
	);
}
